import React, { Fragment, memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { AdminUpdateRetryConfigTemplate } from 'src/constants/actionTypes'
import { retryActionTypeTitles } from 'src/constants/scheduler/constants'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import type { RetryConfigTemplate } from 'src/interfaces/retryConfigTemplate'
import { getRetryConfigTemplateSaving } from 'src/selectors/retryConfigTemplate'
import { updateRetryTemplateValidator } from './UpdateRetryTemplateModal.validator'

const FORM_ID = 'update-retry-template-form'

interface Props {
  /** `null` closes the modal. */
  template: RetryConfigTemplate | null
  onClose(): void
}

interface FormValues {
  maxAttempts: number | ''
  backoffMultiplier: number | ''
  timeoutMinutes: number | ''
}

const buildInitialValues = (template: RetryConfigTemplate | null): FormValues => {
  return {
    maxAttempts: template?.maxAttempts ?? '',
    backoffMultiplier: template?.backoffMultiplier ?? '',
    timeoutMinutes: template?.timeoutMinutes ?? '',
  }
}

/**
 * `Formik` wraps `CommonModal` so the footer's submit button, which `CommonModal` renders
 * through its `buttons` slot outside the `<Form>` element, still reads the form's gate through
 * context (same shape as `AiReviewConfigFormModal` / `SendTestWebhookModal`).
 */
const UpdateRetryTemplateModalComponent = ({ template, onClose }: Props) => {
  const isSaving = useSelector(getRetryConfigTemplateSaving)
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const snackbar = useSnackbar()

  const initialValues = useMemo(() => {
    return buildInitialValues(template)
  }, [template])

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      if (!template) {
        return
      }

      // Keyed by `type`, not `id` — the route is `/templates/:retryActionType`. Sending the
      // row's `id` 404s.
      const result = await asyncDispatchWithErrorToast(
        AdminUpdateRetryConfigTemplate.request({
          retryActionType: template.type,
          maxAttempts: Number(values.maxAttempts),
          backoffMultiplier: Number(values.backoffMultiplier),
          timeoutMinutes: Number(values.timeoutMinutes),
        }),
      )

      if (!result) {
        return
      }

      snackbar?.enqueueSnackbar('Retry template updated', { variant: 'success' })
      onClose()
    },
    [asyncDispatchWithErrorToast, onClose, snackbar, template],
  )

  if (!template) {
    // Unmounting resets Formik, so the next open starts from the right row's values.
    return null
  }

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={updateRetryTemplateValidator}
      onSubmit={handleSubmit}
    >
      <CommonModal
        isOpen={Boolean(template)}
        title={`Update template: ${retryActionTypeTitles[template.type] ?? template.type}`}
        onClose={onClose}
        buttons={
          <Fragment>
            <Button
              size="small"
              color="grey"
              onClick={onClose}
              data-testid="update-retry-template-cancel-button"
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="dark"
              type="submit"
              form={FORM_ID}
              loading={isSaving}
              disabled={isSaving}
              data-testid="update-retry-template-save-button"
            >
              Save template
            </Button>
          </Fragment>
        }
      >
        <Form id={FORM_ID} data-testid="update-retry-template-form">
          <div css={tw`flex flex-col gap-4`}>
            <FormikTextField<FormValues>
              name="maxAttempts"
              label="Max attempts"
              type="number"
              data-testid="update-retry-template-max-attempts-input"
            />
            <FormikTextField<FormValues>
              name="backoffMultiplier"
              label="Backoff multiplier"
              type="number"
              data-testid="update-retry-template-backoff-multiplier-input"
            />
            <FormikTextField<FormValues>
              name="timeoutMinutes"
              label="Timeout (minutes)"
              type="number"
              data-testid="update-retry-template-timeout-minutes-input"
            />
          </div>
        </Form>
      </CommonModal>
    </Formik>
  )
}

export const UpdateRetryTemplateModal = memo(UpdateRetryTemplateModalComponent)
